import { useQuery } from "@tanstack/react-query";
import { getAdminAvailableSlots, getAdminBookableDates } from "@/lib/admin-booking.functions";
import { getAvailableSlots, getBookableDates, getLessonTypes } from "@/lib/booking.functions";
import { getSitePrices } from "@/lib/pricing.functions";
import { calendarSelectionToSiteKey, siteMonthBounds } from "@/lib/datetime.schedule";

type WizardQueryOptions = {
  lessonTypeId?: string;
  date?: Date;
  calendarMonth: Date;
  skipCutoff: boolean;
  enabled?: boolean;
  bookableEnabled?: boolean;
  slotsEnabled?: boolean;
};

export function useBookingWizardQueries({
  lessonTypeId,
  date,
  calendarMonth,
  skipCutoff,
  enabled = true,
  bookableEnabled,
  slotsEnabled,
}: WizardQueryOptions) {
  const lessonTypesQ = useQuery({
    queryKey: ["lesson-types"],
    queryFn: () => getLessonTypes(),
    enabled,
  });

  const sitePricesQ = useQuery({
    queryKey: ["site-prices"],
    queryFn: () => getSitePrices(),
    enabled,
  });

  const monthBounds = siteMonthBounds(calendarMonth);
  const bookableDatesQ = useQuery({
    queryKey: ["bookable-dates", monthBounds.start, monthBounds.end, lessonTypeId, skipCutoff],
    queryFn: () => {
      const data = {
        start: monthBounds.start,
        end: monthBounds.end,
        lessonTypeId: lessonTypeId!,
      };
      return skipCutoff ? getAdminBookableDates({ data }) : getBookableDates({ data });
    },
    enabled: (bookableEnabled ?? enabled) && !!lessonTypeId,
  });

  const dateKey = date ? calendarSelectionToSiteKey(date) : null;
  const slotsQ = useQuery({
    queryKey: ["slots", dateKey, lessonTypeId, skipCutoff],
    queryFn: () => {
      const data = { date: dateKey!, lessonTypeId: lessonTypeId! };
      return skipCutoff ? getAdminAvailableSlots({ data }) : getAvailableSlots({ data });
    },
    enabled: (slotsEnabled ?? enabled) && !!date && !!lessonTypeId,
  });

  return { lessonTypesQ, sitePricesQ, bookableDatesQ, slotsQ, monthBounds, dateKey };
}
